import {EdgesGeometry, Mesh, LineSegments, Color} from "three";
import {CircleGeometry} from "three/src/geometries/CircleGeometry.js";
import {Constants} from "../Constants";
import {BasicMaterial} from "../materials/BasicMaterial";
import {THREEUtils} from "../../../../../../utils/THREEUtils";
import type {EditableSpaceItem} from "./EditableSpaceItem";

interface IGrabbableCornerStyle {
	fillColor?: number;
	fillOpacity?: number;
	strokeColor?: number;
	sizePx?: number;
	useFilledOutline?: boolean;
	innerScale?: number;
}

export class GrabbableCorner {
	private static readonly _RADIUS: number = 0.4;
	private _geometry: CircleGeometry = new CircleGeometry(GrabbableCorner._RADIUS, 16);
	private _material: BasicMaterial;
	private _mesh: Mesh;
	private _edges: EdgesGeometry = new EdgesGeometry(this._geometry);
	private _lineSegments: LineSegments | null = null;
	private _fillMesh: Mesh;
	private readonly _sizePx: number;

	constructor(
		spaceItem: EditableSpaceItem,
		worldX: number,
		worldY: number,
		strokeColor: number,
		meshName: string,
		style: IGrabbableCornerStyle = {},
	) {
		this._sizePx = style.sizePx ?? Constants.SIZE.GRABBABLE_ITEM_PX;
		this._material = new BasicMaterial(style.fillColor ?? 0xffffff, style.fillOpacity ?? 0.5, true);
		this._mesh = new Mesh(this._geometry, this._material);
		this._fillMesh = this._mesh;

		if (style.useFilledOutline) {
			this._material.setColor(style.strokeColor ?? strokeColor);
			const innerMaterial = new BasicMaterial(style.fillColor ?? 0xffffff, style.fillOpacity ?? 1, true);
			const innerMesh = new Mesh(this._geometry, innerMaterial);

			innerMesh.scale.setScalar(style.innerScale ?? 0.72);
			THREEUtils.updateMatrices(innerMesh);
			this._mesh.add(innerMesh);
			this._fillMesh = innerMesh;
		} else {
			this._lineSegments = new LineSegments(this._edges, new BasicMaterial(style.strokeColor ?? strokeColor, 1, false));
			this._lineSegments.name = "excluded"; // excluded from raycast intersections
			this._mesh.add(this._lineSegments);
		}

		this._mesh.name = meshName;
		this._mesh.position.set(worldX, worldY, 0);
		this._mesh.userData.grabbableCorner = this;
		this._mesh.userData.itemManager = spaceItem.itemManager;
		THREEUtils.add(spaceItem.intersectables, this._mesh);
	}

	public setSize(size: number) {
		this._mesh.scale.setX(size);
		this._mesh.scale.setY(size);
		THREEUtils.updateMatrices(this._mesh);
	}

	public setFillColor(color: number, intensity: number) {
		const colorObj = new Color(color);

		colorObj.multiplyScalar(intensity);
		(this._fillMesh.material as BasicMaterial).setColor(colorObj.getHex());
	}

	public dispose() {
		this._mesh.geometry.dispose();
		this._lineSegments?.geometry.dispose();
		THREEUtils.disposeContainer(this._mesh);
	}

	public get mesh() {
		return this._mesh;
	}

	public get sizePx() {
		return this._sizePx;
	}

	public static get RADIUS() {
		return GrabbableCorner._RADIUS;
	}
}
